import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TourProvider, useTour } from '@reactour/tour'
import type { StepType } from '@reactour/tour'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import useResponsive from 'utils/hooks/use-responsive'
import { FontIcon, tokens } from '@arvoreeducacao/bonsai-ds'
import { useQuery } from '@tanstack/react-query'
import {
  readingFluencyControllerCheckAssessmentAvailable,
  onboardingControllerFindOrCreateTutorialEndpoint,
  TutorialResponseDtoState
} from 'api.gen'
import { useTranslation } from 'i18n'
import { getImageUrl } from 'utils/assets/Cdn'
import activateOnEnterOrSpace from 'utils/activate-on-enter-or-space'
import { OnboardingV3Context } from 'context/onboarding-v3'

type TourContentProps = {
  title: string
  onDismiss: () => void
}

const TourContent: React.FC<TourContentProps> = ({ title, onDismiss }) => {
  const { t } = useTranslation()
  const { isDesktop } = useResponsive()

  return (
    <div className="relative flex flex-col gap-6 pt-2">
      {isDesktop && (
        <div className="absolute rounded-small top-0 left-0 w-[21px] h-[21px] bg-white rotate-45 translate-x-[700%] translate-y-[-150%]" />
      )}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between w-full">
          <p className="font-bold text-[20px] leading-[1.3] text-gray-900 m-0 flex-1">
            {title}
          </p>
          <div
            className="cursor-pointer p-2 rounded-large hover:bg-gray-100"
            role="button"
            tabIndex={0}
            aria-label={t('Fechar')}
            onKeyDown={activateOnEnterOrSpace(onDismiss)}
            onClick={onDismiss}
          >
            <FontIcon iconName="cancel" size={16} />
          </div>
        </div>
        <div className="w-full h-[148px] rounded-large overflow-hidden flex items-center justify-center">
          <img
            className="w-full h-full object-cover"
            src={getImageUrl(
              'fluencia-leitora/spotlight-missao-atividades.png'
            )}
            alt={t('Missão de fluência leitora')}
          />
        </div>
        <div className="pt-[10px]">
          <p className="text-[14px] leading-[1.3] text-gray-700 m-0">
            {t('Valendo uma')}{' '}
            <span className="font-bold">{t('recompensa especial')}</span>!
          </p>
        </div>
      </div>
    </div>
  )
}

type TourProps = {
  currentSelector: string
  onDismiss: () => void
}

const Tour: React.FC<TourProps> = ({ currentSelector, onDismiss }) => {
  const { t } = useTranslation()
  const { setSteps, setIsOpen } = useTour()
  const { isDesktop } = useResponsive()

  useEffect(() => {
    const steps: StepType[] = [
      {
        selector: currentSelector,
        position: isDesktop ? 'bottom' : 'top',
        content: (
          <TourContent
            title={t('Tem uma missão te esperando!')}
            onDismiss={onDismiss}
          />
        )
      }
    ]
    setSteps(steps)
  }, [setSteps, currentSelector, onDismiss, isDesktop, t])

  useEffect(() => {
    setIsOpen(true)
  }, [setIsOpen])

  return null
}

const STORAGE_KEY = 'activities-first-assessment-spotlight-dismissed'

const getCurrentYear = (): number => new Date().getFullYear()

const getDismissedKey = (userId: string): string =>
  `${STORAGE_KEY}-${userId}-${getCurrentYear()}`

const isDismissedForUser = (userId: string): boolean =>
  localStorage.getItem(getDismissedKey(userId)) === 'true'

const setDismissedForUser = (userId: string): void =>
  localStorage.setItem(getDismissedKey(userId), 'true')

type ActivitiesFirstAssessmentSpotlightProps = {
  children: React.ReactNode
}

const ActivitiesFirstAssessmentSpotlight: React.FC<
  ActivitiesFirstAssessmentSpotlightProps
> = ({ children }) => {
  const { t } = useTranslation()
  const { isDesktop } = useResponsive()
  const [isVisible, setIsVisible] = useState(false)
  const { user } = useUserInfo()
  const { openModalCount } = React.useContext(OnboardingV3Context)
  const previousOpenModalCountRef = useRef(openModalCount)

  const canAccessActivities = useMemo(() => {
    return Boolean(user?.canAccessActivities)
  }, [user])

  const canAccessReadingFluency = useMemo(() => {
    return Boolean(user?.canAccessReadingFluency)
  }, [user])

  const canShowSpotlight = useMemo(() => {
    return canAccessActivities && canAccessReadingFluency
  }, [canAccessActivities, canAccessReadingFluency])

  const isAlreadyDismissed = useMemo(() => {
    return isDismissedForUser(user?.id)
  }, [user?.id])

  const canAccessOnboarding = Boolean(user?.canAccessOnboarding)
  const hasOnboardingV3Enabled = Boolean(
    user?.featureToggles?.onboardingAlunoV3
  )
  const shouldCheckOnboardingTutorial =
    canAccessOnboarding && hasOnboardingV3Enabled

  const {
    data: checklistTutorialData,
    isLoading: isLoadingTutorial,
    refetch: refetchTutorial
  } = useQuery({
    queryKey: ['checklist-onboarding-status-for-spotlight'],
    queryFn: async () => {
      const response = await onboardingControllerFindOrCreateTutorialEndpoint({
        name: 'checklist_onboarding'
      })
      if (response.status !== 400 && response.data) {
        return response.data
      }
      return null
    },
    refetchOnWindowFocus: false,
    enabled:
      shouldCheckOnboardingTutorial && canShowSpotlight && !isAlreadyDismissed
  })

  useEffect(() => {
    const wasModalOpen = previousOpenModalCountRef.current > 0
    const isModalClosed = openModalCount === 0

    if (wasModalOpen && isModalClosed && shouldCheckOnboardingTutorial) {
      setTimeout(() => {
        refetchTutorial()
      }, 500)
    }

    previousOpenModalCountRef.current = openModalCount
  }, [openModalCount, shouldCheckOnboardingTutorial, refetchTutorial])

  const isOnboardingChecklistCompleted = useMemo(() => {
    if (!shouldCheckOnboardingTutorial) return true
    if (isLoadingTutorial) return false

    const tutorialState = checklistTutorialData?.state
    return tutorialState === TutorialResponseDtoState.completed
  }, [shouldCheckOnboardingTutorial, isLoadingTutorial, checklistTutorialData])

  const { data: assessmentData, isLoading: isLoadingAssessment } = useQuery({
    queryKey: ['reading-fluency-assessment-available-spotlight'],
    queryFn: () => readingFluencyControllerCheckAssessmentAvailable(),
    refetchOnWindowFocus: false,
    enabled:
      canShowSpotlight && !isAlreadyDismissed && isOnboardingChecklistCompleted
  })

  const getActivitiesSelector = useCallback(() => {
    if (!isDesktop) {
      const bottomNavElement = document.querySelector(
        '[data-testid="bottom-navigation-item-clipboard-content"]'
      )
      if (bottomNavElement)
        return '[data-testid="bottom-navigation-item-clipboard-content"]'

      const bottomNavFilled = document.querySelector(
        '[data-testid="bottom-navigation-item-clipboard-content-filled"]'
      )
      if (bottomNavFilled)
        return '[data-testid="bottom-navigation-item-clipboard-content-filled"]'

      return null
    }

    const atividadesElement = document.querySelector(
      `[id="${t('Atividades')}"]`
    )
    if (atividadesElement) return `[id="${t('Atividades')}"]`

    const tarefasElement = document.querySelector(`[id="${t('Tarefas')}"]`)
    if (tarefasElement) return `[id="${t('Tarefas')}"]`

    return null
  }, [t, isDesktop])

  const [currentSelector, setCurrentSelector] = useState<string | null>(null)
  const hasShownRef = useRef(false)

  const isLoading =
    isLoadingAssessment || (shouldCheckOnboardingTutorial && isLoadingTutorial)

  useEffect(() => {
    if (
      isLoading ||
      !canShowSpotlight ||
      isAlreadyDismissed ||
      isVisible ||
      hasShownRef.current ||
      !isOnboardingChecklistCompleted
    ) {
      return
    }

    const isFirstAssessment = assessmentData?.data?.first === true

    if (!isFirstAssessment) {
      return
    }

    hasShownRef.current = true

    const selector = getActivitiesSelector()
    if (selector) {
      setCurrentSelector(selector)
      setIsVisible(true)
    }
  }, [
    assessmentData,
    isLoading,
    canShowSpotlight,
    getActivitiesSelector,
    isAlreadyDismissed,
    isOnboardingChecklistCompleted,
    isVisible
  ])

  const onDismiss = useCallback(() => {
    setDismissedForUser(user?.id)
    setIsVisible(false)
  }, [user?.id])

  return (
    <TourProvider
      key={`activities-first-assessment-spotlight-${isVisible}-${isDesktop}`}
      steps={[]}
      showCloseButton={false}
      showDots={false}
      showPrevNextButtons={false}
      styles={{
        controls: base => ({ ...base, marginTop: 0 }),
        maskArea: (base, style) => ({
          ...base,
          rx: isDesktop ? 10 : 30,
          y: style?.y + 5,
          x: style?.x + 5,
          width: style?.width - 10,
          height: isDesktop ? style?.height - 30 : style?.height - 10,
          fill: '#000'
        }),
        popover: base => {
          if (!isDesktop) {
            return {
              ...base,
              minWidth: 320,
              maxWidth: 328,
              marginLeft: '-20px',
              marginBottom: '90px',
              padding: tokens.spacing.insetLarge,
              borderRadius: `${tokens.radius.radius_20}px`,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)'
            }
          }

          const popoverWidth = 360
          let marginLeft = -160
          const marginLeftRatio = 2.65

          const element = document.querySelector(currentSelector || '')
          if (element) {
            const elementRect = element.getBoundingClientRect()
            marginLeft = (elementRect.width - popoverWidth) / marginLeftRatio
          }

          return {
            ...base,
            borderRadius: '20px',
            padding: 24,
            width: `${popoverWidth}px`,
            boxShadow: '0px 24px 38px 3px rgba(5, 59, 75, 0.06)',
            marginLeft: `${marginLeft}px`
          }
        },
        badge: () => ({
          display: 'none'
        })
      }}
      afterOpen={() => {
        const scrollY = window.scrollY
        document.body.style.position = 'fixed'
        document.body.style.top = `-${scrollY}px`
        document.body.style.width = '100%'
        document.body.style.overflowY = 'scroll'
        window.history.scrollRestoration = 'manual'
        window.scrollTo(0, scrollY)
      }}
      beforeClose={() => {
        onDismiss()
        const scrollY = document.body.style.top
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflowY = ''
        if (scrollY) {
          window.scrollTo(0, parseInt(scrollY || '0') * -1)
        }
        window.history.scrollRestoration = 'auto'
      }}
    >
      {children}
      {isVisible && currentSelector && (
        <Tour currentSelector={currentSelector} onDismiss={onDismiss} />
      )}
    </TourProvider>
  )
}

export default ActivitiesFirstAssessmentSpotlight
